<template>
  <!--
    `soMiniaturas`: o picker dentro do composer da resposta. O botão de imagem
    é o do próprio composer (que chega aqui por `receber`), então daqui só
    sobem as miniaturas e o aviso de limite — e nada, quando não há imagem.
  -->
  <div
    v-explain.top="blocked"
    class="anexo-picker"
    :class="{ 'anexo-picker--vazio': soMiniaturas && previas.length === 0 && !cheio }"
  >
    <div v-if="previas.length > 0" class="anexo-miniaturas">
      <div v-for="(p, i) in previas" :key="p.url" class="anexo-miniatura">
        <img :src="p.url" :alt="p.nome" />
        <button
          type="button"
          v-explain.left="EX.removeAttachment"
          class="anexo-remover"
          :aria-label="t('Remover imagem')"
          :data-testid="`chamados-anexo-remover-${i}`"
          @click="remover(i)"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>
    </div>

    <!--
      Input NATIVO escondido + label como botão: é o padrão já usado pelo
      `ServicoPhotoUpload` (fotos de serviço), e é o único jeito de ter um
      gatilho estilizado. Um componente de input da casa não serve aqui —
      `<file-model>` é um arquivo só, sem `accept`, sem miniatura e sem
      remoção, que é justamente o que esta tela precisa.
    -->
    <input
      :id="idDoInput"
      ref="inputRef"
      type="file"
      accept="image/*"
      multiple
      v-explain.top="blocked"
      class="anexo-input-escondido"
      :disabled="disabled || cheio"
      :data-testid="testid"
      @change="aoEscolher"
    />
    <label
      v-if="!soMiniaturas"
      :for="idDoInput"
      class="anexo-botao"
      :class="{ 'anexo-botao--off': disabled || cheio }"
      :data-testid="`${testid}-label`"
    >
      <i :class="processando ? 'pi pi-spin pi-spinner' : 'pi pi-image'"></i>
      {{ processando ? t('Preparando imagem...') : rotulo }}
    </label>

    <small v-if="cheio" class="anexo-aviso">
      {{ t('Limite de {n} imagens por mensagem', { n: MAX_ANEXOS_POR_MENSAGEM }) }}
    </small>
    <small v-else-if="dica && !soMiniaturas" class="anexo-dica">{{ dica }}</small>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { ExplanationInput } from '../../explanations/types';
import { ticketExplanations } from './explanations';
import { eventBus } from '../../eventBus';
import { compressImage } from '../../utils/compressImage';
import { log } from '../../utils/DebugLogger';
import {
  MAX_ANEXOS_POR_MENSAGEM,
  MIMES_DE_IMAGEM,
  TAMANHO_MAX_ANEXO,
} from '../../composables/useChamados';

/**
 * ChamadosImagePicker — escolher as imagens ANTES de elas terem dono.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ELE SÓ SEGURA `File`, E NÃO SOBE NADA
 * ---------------------------------------------------------------------------
 * O anexo pertence a uma MENSAGEM (`chamados_anexos.mensagem_id` é
 * obrigatório), e na abertura a mensagem só existe DEPOIS que o chamado é
 * criado. Se este componente subisse na hora da escolha, teria de subir para
 * lugar nenhum e amarrar depois — e todo arquivo de um formulário abandonado
 * ficaria no Blob para sempre, pago e órfão.
 *
 * Então ele acumula `File` em memória e quem o usa sobe quando já há mensagem.
 * O mesmo componente serve à abertura e à resposta sem nenhuma variação.
 *
 * ---------------------------------------------------------------------------
 * A COMPRESSÃO É AQUI, NÃO NO SERVIDOR
 * ---------------------------------------------------------------------------
 * `compressImage` (1920 px, qualidade 0,8 — o mesmo utilitário das fotos de
 * serviço) roda no NAVEGADOR. Foto de celular moderno passa de 5 MB com
 * facilidade e seria recusada pelo backend; comprimida, a mesma foto vira
 * algumas centenas de KB sem perda visível num print ou numa foto de defeito.
 *
 * Comprimir depois do upload não resolveria o problema: o que trava é a
 * SUBIDA, na internet da franquia, não o disco do servidor.
 *
 * ---------------------------------------------------------------------------
 * A VALIDAÇÃO AQUI É CONVENIÊNCIA, NÃO SEGURANÇA
 * ---------------------------------------------------------------------------
 * Quem recusa de verdade é o backend, que lê os BYTES (`getimagesize`). O
 * `accept="image/*"` é ignorável com um clique no seletor do sistema
 * operacional, e `file.type` vem do sistema operacional do usuário. Isto aqui
 * só evita a viagem inútil e dá o erro na hora.
 */

const props = withDefaults(
  defineProps<{
    modelValue: File[];
    disabled?: boolean;
    rotulo?: string;
    testid?: string;
    /** Linha de ajuda logo abaixo do botão. Vazia = nenhuma. */
    dica?: string;
    /** Sem botão nem dica: quem escolhe a imagem é o composer em volta. */
    soMiniaturas?: boolean;
    /** Why the picker is disabled right now (skill `self-explaining-ui`); null when it is not. */
    explanation?: ExplanationInput;
  }>(),
  {
    disabled: false,
    rotulo: '',
    testid: 'chamados-anexo-input',
    dica: '',
    soMiniaturas: false,
    explanation: null,
  },
);

const emit = defineEmits<{
  'update:modelValue': [arquivos: File[]];
}>();

const { t } = useI18n();

const inputRef = ref<HTMLInputElement | null>(null);
const processando = ref(false);

/**
 * Id único por instância: o `<label for>` precisa apontar para ESTE input, e a
 * tela de detalhe pode ter o picker da resposta no ar junto com outro.
 */
const idDoInput = `chamados-anexo-${Math.random().toString(36).slice(2, 9)}`;

const rotulo = computed(() =>
  props.rotulo || (props.modelValue.length === 0 ? t('Anexar imagem') : t('Adicionar mais')),
);

const cheio = computed(() => props.modelValue.length >= MAX_ANEXOS_POR_MENSAGEM);

const EX = computed(() => ticketExplanations(t));

/** Why no image can be added right now: the caller's reason first, else the per-message limit. */
const blocked = computed((): ExplanationInput => {
  if (props.explanation) return props.explanation;
  if (cheio.value) return EX.value.attachmentLimit;

  return null;
});

/**
 * As miniaturas.
 *
 * `URL.createObjectURL` cria uma entrada no mapa de blobs do documento que
 * SÓ some com `revokeObjectURL` — sem revogar, cada foto escolhida e removida
 * fica retida em memória até a aba fechar. Por isso a lista é reconstruída num
 * `watch` que revoga a anterior, em vez de num `computed` (que não teria onde
 * limpar).
 */
const previas = ref<Array<{ url: string; nome: string }>>([]);

const revogar = (): void => {
  previas.value.forEach((p) => URL.revokeObjectURL(p.url));
};

watch(
  () => props.modelValue,
  (arquivos) => {
    revogar();
    previas.value = arquivos.map((a) => ({ url: URL.createObjectURL(a), nome: a.name }));
  },
  { immediate: true, deep: true },
);

onBeforeUnmount(revogar);

const aoEscolher = async (evento: Event): Promise<void> => {
  const input = evento.target as HTMLInputElement;
  const escolhidos = Array.from(input.files ?? []);

  // Zera o input SEMPRE: sem isso, escolher o mesmo arquivo duas vezes
  // seguidas não dispara `change` na segunda (o valor não mudou), e o usuário
  // conclui que a tela travou.
  input.value = '';

  await receberVarios(escolhidos);
};

/**
 * A porta de entrada de arquivos, independente de QUAL gesto os trouxe.
 *
 * O botão de anexo chama por aqui, e o editor rich text também (colar,
 * arrastar, ou o botão de imagem da barra) — assim o limite, a compressão, a
 * validação e a miniatura são os mesmos nos dois caminhos. Um segundo caminho
 * com regras próprias é como um deles acaba aceitando o que o outro recusa.
 */
const receberVarios = async (escolhidos: File[]): Promise<void> => {
  if (escolhidos.length === 0) return;

  processando.value = true;

  try {
    const aceitos: File[] = [];

    for (const arquivo of escolhidos) {
      if (props.modelValue.length + aceitos.length >= MAX_ANEXOS_POR_MENSAGEM) {
        eventBus.emit(
          'globalMessage.error',
          t('Limite de {n} imagens por mensagem', { n: MAX_ANEXOS_POR_MENSAGEM }),
        );
        break;
      }

      if (!MIMES_DE_IMAGEM.includes(arquivo.type)) {
        eventBus.emit(
          'globalMessage.error',
          t('"{nome}" não é uma imagem aceita (JPEG, PNG, WebP ou GIF)', { nome: arquivo.name }),
        );
        continue;
      }

      const comprimido = await compressImage(arquivo);

      // Mesmo comprimida a imagem pode passar do teto (uma foto enorme de
      // scanner, por exemplo). Barrar aqui evita o 400 do backend.
      if (comprimido.size > TAMANHO_MAX_ANEXO) {
        eventBus.emit(
          'globalMessage.error',
          t('"{nome}" continua acima de 5 MB mesmo depois de comprimida', { nome: arquivo.name }),
        );
        continue;
      }

      log('ChamadosImagePicker', 'aoEscolher', 'Imagem pronta', {
        nome: arquivo.name,
        antes: arquivo.size,
        depois: comprimido.size,
      });

      aceitos.push(comprimido);
    }

    if (aceitos.length > 0) {
      emit('update:modelValue', [...props.modelValue, ...aceitos]);
    }
  } catch (e) {
    const detalhe = e instanceof Error ? e.message : String(e);
    eventBus.emit('globalMessage.error', t('Erro ao preparar a imagem') + ': ' + detalhe);
  } finally {
    processando.value = false;
  }
};

/** Um arquivo só — o que o editor emite a cada imagem colada/arrastada. */
const receber = async (arquivo: File): Promise<void> => {
  await receberVarios([arquivo]);
};

defineExpose({ receber });

const remover = (indice: number): void => {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== indice),
  );
};
</script>

<style scoped>
.anexo-picker {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.anexo-picker--vazio {
  display: none;
}

/*
  A IMAGEM APARECE DO TAMANHO QUE ELA É, empilhada — igual ao formulário do
  Odoo, onde a foto entra no corpo da mensagem em largura cheia
  (`:deep(.tiptap img) { max-width: 100%; display: block; margin: 10px 0 }`),
  e não como uma pastilha de canto.
  A razão é prática: num chamado a imagem É o conteúdo. Quem abre manda o
  print do erro, e uma miniatura de 64px não deixa conferir se é a tela certa
  ANTES de enviar — que é justamente quando dá para trocar.
*/
.anexo-miniaturas {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.anexo-miniatura {
  position: relative;
  max-width: 100%;
  align-self: flex-start;
}

.anexo-miniatura img {
  max-width: 100%;
  /* Teto de altura para uma foto de celular em pé não empurrar o botão de
     enviar para fora da tela. `contain` e não `cover`: cortar o print seria
     esconder justamente a parte que o usuário quer mostrar. */
  max-height: 320px;
  object-fit: contain;
  display: block;
  border-radius: 5px;
  border: 1px solid #e0e0e0;
}

.anexo-remover {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 0.72rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/*
  O input fica FORA DE VISTA, mas NÃO com `display: none` nem `hidden`: assim
  ele some da ordem de foco e o teclado não alcança o anexo. Esta é a receita
  acessível — invisível ao olho, presente para o leitor de tela.
*/
.anexo-input-escondido {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/*
  Discreto DE PROPÓSITO: dentro do campo ele divide espaço com o texto da
  mensagem, que é o assunto principal. Um botão sólido aqui puxaria a atenção
  para o anexo, que é o acessório.
*/
.anexo-botao {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border: 1px dashed #c9d1d9;
  border-radius: 6px;
  font-size: 0.76rem;
  color: #5b6672;
  cursor: pointer;
  background: transparent;
}

.anexo-botao:hover {
  border-color: #7d8b99;
}

.anexo-input-escondido:focus-visible + .anexo-botao {
  outline: 2px solid #2f6feb;
  outline-offset: 2px;
}

.anexo-botao--off {
  opacity: 0.5;
  cursor: not-allowed;
}

.anexo-aviso {
  color: #8a6d3b;
  font-size: 0.72rem;
}

.anexo-dica {
  color: #8a949e;
  font-size: 0.72rem;
}
</style>
